import { useForm, useFormContext } from 'react-hook-form';
import { allPass, complement, compose, dissoc, equals, filter, includes, join, pathOr, prop, propOr } from 'ramda';
import { useEffect, useState } from 'react';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { GranteeTypeEnum } from '@fasstech/oip-core/entities/Grantee/enums';
import { attachedToOptions } from '@@viewCommonComponents/forms/options/index.js';
import { attachedToEnum } from '@@viewEnums';

const useGrantee = ({ granteeIndex, middleFeatures, getMiddleText, getMiddleBoolean }) => {
  const [displayReferentModal, setDisplayReferentModal] = useState(false);
  const form = useFormContext();

  const referentForm = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      grantees: form.getValues('grantees')
    }
  });

  const granteeType = form.getValues(`grantees.${granteeIndex}.type`);
  const granteeBirthdate = form.getValues(`grantees.${granteeIndex}.birthDate`);
  const granteeTypeLabel = getMiddleText(`BENEFICIAIRE_TYPE_${granteeType}_LABEL`);
  const onSubmitReferentForm = async () => {
    const values = referentForm.getValues();
    const referent = pathOr({}, ['grantees', granteeIndex, 'attachedSocialSecurities', 0], values);
    form.setValue(
      getFieldsName('attachedSocialSecurities.0.firstName'),
      prop('firstName', referent)
    );
    form.setValue(getFieldsName('attachedSocialSecurities.0.lastName'), prop('lastName', referent));
    form.setValue(getFieldsName('attachedSocialSecurities.0.civility'), prop('civility', referent));
    form.setValue(
      getFieldsName('attachedSocialSecurities.0.birthDate'),
      prop('birthDate', referent)
    );
    form.setValue(getFieldsName('attachedSocialSecurities.0.number'), prop('number', referent));
    form.setValue(
      getFieldsName('attachedSocialSecurities.0.referent'),
      propOr('', 'lastName', referent) + ' ' + propOr('', 'firstName', referent)
    );
    form.trigger();
    setDisplayReferentModal(false);
  };

  const granteeIsChild = equals(granteeType, GranteeTypeEnum.CHILD);
  const granteeIsSpouse = equals(granteeType, GranteeTypeEnum.SPOUSE);

  const granteeTypeIsEmpty =
    isNilOrEmpty(getMiddleText('BENEFICIAIRE_TYPE_CHILD_LABEL')) ||
    isNilOrEmpty(getMiddleText('BENEFICIAIRE_TYPE_SPOUSE_LABEL'));

  const spouseExists = form
    .watch('grantees')
    .find(({ type }) => equals(type, GranteeTypeEnum.SPOUSE));
  const { BENEFICIAIRE_NSS_RATT_A_LISTE = [] } = middleFeatures;
  const displayedAttachedToOptions = filter(
    ({ value, granteeTypes, needsSpouse }) =>
      includes(value, BENEFICIAIRE_NSS_RATT_A_LISTE) &&
      includes(granteeType, granteeTypes) &&
      (!needsSpouse || spouseExists)
  )(attachedToOptions);

  const getFieldsName = (name) => `grantees.${granteeIndex}.${name}`;

  const currentReferent = dissoc(
    'type',
    form.getValues(getFieldsName('attachedSocialSecurities.0'))
  );

  const attachedToValue = form.watch(getFieldsName('attachedSocialSecurities.0.type'));

  useEffect(() => {
    if (
      allPass([
        isNotNilOrEmpty,
        complement(equals('EN_NOM_PROPRE')),
        () => equals(granteeType, 'CHILD')
      ])(attachedToValue)
    ) {
      form.setValue(getFieldsName('socialSecurities.0.number'), null);
      form.setValue(getFieldsName('socialSecurities.0.organismCode'), null);
    }

    form.clearErrors(getFieldsName('socialSecurities.0'));
  }, [attachedToValue]);

  const firstName = form.watch(
    getFieldsName('attachedSocialSecurities.0.firstName')
  );
  const lastName = form.watch(
    getFieldsName('attachedSocialSecurities.0.lastName')
  );

  useEffect(() => {
    const buildReferent = compose(join(' '), filter(Boolean));
    const referent = buildReferent([lastName, firstName]);

    if (referent) {
      form.setValue(
        getFieldsName('attachedSocialSecurities.0.referent'),
        referent,
        { shouldDirty: false }
      );
    }
  }, [firstName, lastName]);

  const displayGeneralView =
    (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
      (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
        getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION'))) ||
    (getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
      getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE')) ||
    (equals(attachedToEnum.EN_NOM_PROPRE, attachedToValue) &&
      getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_NECESSAIRE') &&
      getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
      (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
        getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION'))) ||
    (getMiddleBoolean('BENEFICIAIRE_RANG_EST_NECESSAIRE') &&
      (getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_CONTRAT') ||
        getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_SOUSCRIPTION'))) ||
    (getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
      !getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE') &&
      (getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_CONTRAT') ||
        getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_SOUSCRIPTION'))) ||
    getMiddleBoolean('BENEFICIAIRE_TELETRANSMISSION_EST_NECESSAIRE');

  return {
    form,
    granteeType,
    granteeIsChild,
    granteeIsSpouse,
    attachedToOptions: displayedAttachedToOptions,
    getFieldsName,
    attachedToValue,
    displayReferentModal,
    setDisplayReferentModal,
    referentForm,
    onSubmitReferentForm,
    currentReferent,
    granteeTypeLabel,
    granteeBirthdate,
    displayGeneralView,
    granteeTypeIsEmpty
  };
};

export default useGrantee;
